// SPEC-SOR-343 (R11/R13/R14/R15) — the wizard's single fee-quote derivation.
//
// CourtFeesTab used to classify each `POST /api/efiling/fees/calculate` outcome
// inline, leave the previous breakdown and context record in place when a
// calculation failed, and render its figures through a client-side fallback
// split — while the Filing Summary rail and Submit bar read the separately
// written `courtFees.fees.totalFees`, so a degraded quote showed court/EFM
// figures beside $0.00 LDS lines and a $0.00 rail. Everything now flows through
// this module (kept dependency-light, React-free, barrel-imports only, mirroring
// `account-fee-waiver-status.ts`, so the spec property probes under
// internal/proptests compile it directly):
//
//   classifyFeeCalcResponse — every response face (and every thrown fetch)
//     lands on one of the shared QuoteState outcomes, carrying the resolved
//     record when — and only when — the calculation resolved whole.
//   feeRecordPatch          — the exact `CourtFeesState` patch the writer
//     applies: a resolved quote writes the record; every other outcome CLEARS
//     it (quote absent, not zeroed — a zero total would waive the payment
//     method the whole-order validator requires, R15).
//   feeInputFingerprint /   — the `QuoteState -> absent` transition the plan's
//     feeQuoteInvalidationPatch  domain model declares (SPEC-SOR-363): a
//     resolved record outlives nothing. The projection serializes the inputs
//     that determine a quote; the decider answers a (previous, next) snapshot
//     pair with the same cleared patch a non-resolved outcome writes.
//   selectResolvedQuote     — the ONLY read path any displayed amount takes;
//     resolvedness is decided by `isFeeQuoteResolved` from the shared-contracts
//     barrel (the one decider, shared with the validator's not-resolved block),
//     never re-derived here.
//
// SPEC-SOR-424: the record is the order's PERSISTED ServerQuote. The fee-calc
// request is order-scoped, and the 200 it answers is the record the server
// composed from that order's own persisted fee facts and persisted — the
// response's five figures ARE the persisted record's five figures
// (`serverQuotedFigures` folds the rendered lines onto `record.total`,
// routes.ts). So `classifyFeeCalcResponse` reading `effectiveFeeQuote` is
// reading the server's record, and `feeRecordPatch` writing it is writing that
// record: no figure on any surface is client-derived, and the filer cannot see
// a number the server will not honor at submit. What this module must never do
// is coalesce, recompute or fall back — a body the server did not return whole
// resolves nothing and clears the record instead.
//
// Response faces consumed (apps/api-gateway/src/features/efiling/fee-quote-state.ts):
// whole quote `{ quoteState: 'resolved' | 'unresolved', effectiveFeeQuote, … }`,
// figure-free degraded `{ quoteState: 'degraded', effectiveFeeQuote: null }`,
// failures 422 `{ error: 'fee_calculation_rejected', message, … }` / 502
// `{ error: 'Fee calculation failed', message }`, both stamped
// `quoteState: 'failed'`. BOTH vendor branches — JTI and Tyler alike since
// SOR-361 — stamp an explicit `quoteState` on every face, so the shape-based
// branch below is a defensive legacy path (an un-versioned fixture body), not
// any live vendor's answer.

import type {
  CourtFeesState,
  EFilingFees,
  EFilingWizardState,
  QuoteState,
  ResolvedFeeQuote,
} from '@lds-efsp/shared-contracts';
import { courtIdentityKey, isFeeQuoteResolved } from '@lds-efsp/shared-contracts';

/** One fee-calculation attempt: the fetch result's ok/status plus its parsed body. `null` is the thrown-fetch face (network-level failure, no HTTP response). */
export interface FeeCalcAttempt {
  ok: boolean;
  status: number;
  body?: unknown;
}

/**
 * The classified outcome of one fee-calculation attempt (SPEC-SOR-343). The
 * record rides ONLY the resolved face. `retryable` is DECLARED, not inferred by
 * the renderer: both degraded and failed are retryable (the r11 property probes
 * this flag), unresolved is the prior SOR-87 blocking path and carries none.
 */
export type FeeQuoteOutcome =
  | { outcome: 'resolved'; quote: ResolvedFeeQuote }
  | { outcome: 'unresolved' }
  | { outcome: 'degraded'; retryable: true }
  | { outcome: 'failed'; retryable: true; message: string; courtRejected: boolean };

/** The legacy vendor fees block the charge path reads — the only part of a response body the patch consumes. */
export interface VendorFeeBreakdown {
  totalAmount?: number;
  lineItems?: Array<{ description?: string; amount: number; category: string }>;
}

/**
 * The ONE all-zero `EFilingFees` literal: the fresh-draft `defaultCourtFees.fees`
 * spreads it (EFilingContext), and every non-resolved outcome resets the legacy
 * `fees` block to a copy of it (`feeRecordPatch`). `EFilingFees` typing on
 * `CourtFeesState.fees` makes shape drift a compile error.
 */
export const ZERO_FEES: EFilingFees = {
  courtFees: 0,
  convenienceFee: 0,
  courtTransactionFee: 0,
  totalCourtFees: 0,
  serviceFee: 0,
  processingFee: 0,
  totalFees: 0,
};

/** The resolved record is exactly the composed quote's five figures — portions plus their total, passed through verbatim in the API's own units. */
function completeQuote(body: unknown): ResolvedFeeQuote | null {
  const quote = body as { effectiveFeeQuote?: unknown } | null | undefined;
  const q = quote?.effectiveFeeQuote;
  if (typeof q !== 'object' || q === null) return null;
  const cand = q as Record<string, unknown>;
  const figure = (key: string): number | null =>
    typeof cand[key] === 'number' && Number.isFinite(cand[key]) ? (cand[key] as number) : null;
  const court = figure('court');
  const efm = figure('efm');
  const ldsService = figure('ldsService');
  const processingFee = figure('processingFee');
  const total = figure('total');
  if (court === null || efm === null || ldsService === null || processingFee === null || total === null) {
    return null;
  }
  return { court, efm, ldsService, processingFee, total };
}

/** The quote's explicit `unresolved: true` degrade flag (the SOR-87 LDS-row miss). */
function explicitlyUnresolved(body: unknown): boolean {
  const quote = body as { effectiveFeeQuote?: { unresolved?: unknown } } | null | undefined;
  return quote?.effectiveFeeQuote?.unresolved === true;
}

/** First present string among the body's message-carrying fields, else the fallback. */
function messageOf(body: unknown, fallback: string): string {
  const b = body as { message?: unknown; error?: unknown } | null | undefined;
  if (typeof b?.message === 'string' && b.message) return b.message;
  if (typeof b?.error === 'string' && b.error) return b.error;
  return fallback;
}

/**
 * Classify one fee-calculation attempt onto the shared QuoteState outcomes.
 * Precedence, pinned by the lib test: a non-OK status is a failure (422 is a
 * court business rejection, WOL-462); otherwise an explicit `quoteState`
 * decides; the `unresolved: true` quote flag forces unresolved over a
 * `resolved` stamp (mirrors the portal's former `quote?.unresolved` read);
 * a response with NO `quoteState` classifies by shape — a complete quote is
 * resolved, anything else is degraded. A thrown fetch (`attempt === null`) is
 * a failure. A `resolved` stamp over an incomplete quote degrades defensively:
 * the API always sends the five figures, so this face exists to keep a partial
 * body from ever writing a record.
 */
export function classifyFeeCalcResponse(attempt: FeeCalcAttempt | null): FeeQuoteOutcome {
  if (attempt === null) {
    return {
      outcome: 'failed',
      retryable: true,
      message: 'Failed to connect to fee calculation service',
      courtRejected: false,
    };
  }
  if (!attempt.ok) {
    if (attempt.status === 422) {
      return {
        outcome: 'failed',
        retryable: true,
        message: messageOf(attempt.body, 'The court rejected this request. Review the filing details and try again.'),
        courtRejected: true,
      };
    }
    return {
      outcome: 'failed',
      retryable: true,
      message: messageOf(attempt.body, 'Fee calculation failed'),
      courtRejected: false,
    };
  }
  const state = (attempt.body as { quoteState?: unknown } | undefined)?.quoteState;
  if (state === 'unresolved') return { outcome: 'unresolved' };
  if (state === 'degraded') return { outcome: 'degraded', retryable: true };
  if (explicitlyUnresolved(attempt.body)) return { outcome: 'unresolved' };
  const quote = completeQuote(attempt.body);
  if (state === 'resolved') {
    return quote ? { outcome: 'resolved', quote } : { outcome: 'degraded', retryable: true };
  }
  // No quoteState on the 200 (legacy fixture body — both vendor branches stamp
  // it since SOR-361): classify by shape.
  return quote ? { outcome: 'resolved', quote } : { outcome: 'degraded', retryable: true };
}

/**
 * The cleared-record shape: the EXPLICIT `quote: undefined` key (`updateCourtFees`
 * shallow-merges, so an omitted key would strand the previous record — precisely
 * the stale-record defect R15 closes) plus the zeroed legacy `fees` block, with
 * the caller's `QuoteState` stamp. ONE cleared-shape authority: `feeRecordPatch`
 * stamps it with a classified outcome, `feeQuoteInvalidationPatch` with
 * `'absent'` — the type's declared "no calculation has run for this draft" state.
 * `authorizePayment` is deliberately absent: a stale-record clear invalidates the
 * QUOTE, never the filer's authorization act.
 */
export function clearedFeeRecordPatch(quoteState: QuoteState = 'absent'): Partial<CourtFeesState> {
  return { quoteState, quote: undefined, fees: { ...ZERO_FEES } };
}

/**
 * The `CourtFeesState` patch the writer applies for a classified outcome.
 * Resolved: the record copy plus the legacy `fees` block exactly as the charge
 * seam reads it (`totalCourtFees`/`courtTransactionFee` from the vendor block,
 * `totalFees` equal to the record's total). EVERY other outcome carries the
 * explicit `quote: undefined` key — `updateCourtFees` shallow-merges, so an
 * omitted key would leave the previous record in place, which is precisely the
 * stale-record defect R15 closes — and resets every legacy fee amount to zero.
 */
export function feeRecordPatch(
  outcome: FeeQuoteOutcome,
  vendorFees?: VendorFeeBreakdown | null,
): Partial<CourtFeesState> {
  if (outcome.outcome !== 'resolved') {
    return clearedFeeRecordPatch(outcome.outcome);
  }
  const lineItems = Array.isArray(vendorFees?.lineItems) ? vendorFees.lineItems : [];
  const courtTransactionFee = lineItems
    .filter(i => i.category === 'JOURNAL')
    .reduce((sum, i) => sum + i.amount, 0);
  const totalAmount = vendorFees?.totalAmount || 0;
  return {
    quoteState: 'resolved',
    quote: outcome.quote,
    fees: {
      courtFees: totalAmount,
      convenienceFee: 0,
      courtTransactionFee: courtTransactionFee || 0,
      totalCourtFees: totalAmount,
      serviceFee: outcome.quote.ldsService,
      processingFee: outcome.quote.processingFee,
      totalFees: outcome.quote.total,
    },
  };
}

/**
 * The resolved quote's record — or null. The ONE read path any displayed fee
 * amount may take (CourtFeesTab here; the rail and submit bar consume it too):
 * resolvedness comes from the shared decider, so the portal never re-derives
 * it, and a cleared (or never-written) record renders as "no figures" rather
 * than falling back to a stale or zero amount.
 */
export function selectResolvedQuote(courtFees: CourtFeesState): ResolvedFeeQuote | null {
  return isFeeQuoteResolved(courtFees) && courtFees.quote ? courtFees.quote : null;
}

// ── The `QuoteState -> absent` transition (SPEC-SOR-363) ───────────────────
//
// The record is written by the Review Fees step's calculation, but the inputs
// it quotes live on other steps. Without the invalidation below, a resolved
// record outlives the inputs that produced it — and because
// `paymentMethodRequired` keys the card requirement on that record, a stale
// resolved $0.00 waives the card on a filing that genuinely costs money. The
// shape mirrors `reset-court-scoped-case.ts`: a pure projection plus an
// explicit snapshot-pair decider, consumed by an always-mounted effect in
// `EFilingProvider` (the repro reaches Submit without ever remounting
// CourtFeesTab, so a tab-local effect can never see the change).

/** The draft slice the fee quote is computed from. */
export type FeeInputSnapshot = Pick<
  EFilingWizardState,
  'orderInfo' | 'caseInfo' | 'parties' | 'documents'
>;

/**
 * The draft's fee-determining inputs, serialized canonically: a hand-built
 * object with FIXED key order, so equal inputs produce byte-equal JSON and the
 * string compares by value. Deliberately broad — a spurious mismatch costs one
 * recalculation (the Review Fees step re-fires its calculation on mount), while
 * a missed one is the stale-record defect itself:
 *
 *   - every party field that can move a fee (the waiver flags, self-rep, the
 *     identity and class a document's party-class metadata references), and
 *     every document field the envelope prices (its type, lead flag, form
 *     group set and metadata) — an added/removed document changes the array.
 *   - the Where-to-File radios beside the filing type: `eServeCaseParticipants`
 *     (the JTI E_SERVICE tag on every party block),
 *     `serveAfterFiling` (Tyler's EFileAndServe vs EFile filing action) and
 *     `isFirstAppearance` (the JTI EFSP_FIRST_APPEARANCE_PAID tag) — all three
 *     enter the priced envelope, so all three are projected, normalized
 *     through `=== true` so a legacy draft with the key absent reads as false
 *     rather than as a change.
 *   - the court identity is projected through `courtIdentityKey`, so the two
 *     value spaces a draft legitimately holds for one court (the OCR slug and
 *     the courts API display name) compare equal instead of reading as a
 *     change that would wipe a live quote.
 *   - uploadStatus/fileName/gcsPath are kept OUT on purpose: they churn while
 *     the priced envelope stays the same, so projecting them would buy
 *     recalculation noise and invalidate nothing that isn't already priced
 *     differently.
 */
export function feeInputFingerprint(state: FeeInputSnapshot): string {
  return JSON.stringify({
    orderInfo: {
      // The court identity is projected through the ONE canonical comparator,
      // not compared raw: the draft legitimately holds either value space for
      // the same court — the OCR slug ('madera') extraction writes and a
      // resume restores verbatim (normalizeHydratedState does not
      // canonicalize), or the courts API display name ('Madera') the R85
      // canonicalization effect rewrites in place once the county options
      // load. A raw comparison would read that rewrite as an input change and
      // clear a live quote the filer never invalidated.
      state: state.orderInfo.state ? courtIdentityKey(state.orderInfo.state) : null,
      county: state.orderInfo.county ? courtIdentityKey(state.orderInfo.county) : null,
      filingType: state.orderInfo.filingType ?? null,
      eServeCaseParticipants: state.orderInfo.eServeCaseParticipants === true,
      serveAfterFiling: state.orderInfo.serveAfterFiling === true,
      isFirstAppearance: state.orderInfo.isFirstAppearance === true,
    },
    caseInfo: {
      caseCategoryCode: state.caseInfo.caseCategoryCode ?? null,
      caseTypeCode: state.caseInfo.caseType?.code ?? null,
      caseTrackingId: state.caseInfo.caseTrackingId ?? null,
    },
    parties: state.parties.map(party => ({
      id: party.id,
      partyType: party.partyType,
      feeWaiverOnFile: party.feeWaiverOnFile === true,
      requestFeesExemption: party.requestFeesExemption === true,
      isSelfRepresented: party.isSelfRepresented === true,
    })),
    documents: state.documents.map(doc => ({
      id: doc.id,
      isLeadDocument: doc.isLeadDocument === true,
      documentTypeCode: doc.documentType?.code ?? null,
      formGroups: doc.formGroups ?? null,
      metadataValues: doc.metadataValues ?? null,
    })),
  });
}

/**
 * The invalidation decider over a (previous, next) snapshot pair — the same
 * contract `reset-court-scoped-case.ts` requires of its court-change sites: the
 * transition is CHECKED, not assumed from the fact that a render happened.
 * Changed inputs answer with the exact cleared patch a non-resolved outcome
 * writes (`clearedFeeRecordPatch('absent')` — never a second cleared shape);
 * unchanged inputs answer null, so the caller cannot wipe a record for nothing.
 * Whether a record is there to invalidate is the CALLER's gate
 * (`isFeeQuoteResolved`): a `failed`/`degraded`/`unresolved` face and its retry
 * control are nothing this patch may clobber.
 */
export function feeQuoteInvalidationPatch(
  previousFingerprint: string,
  nextFingerprint: string,
): Partial<CourtFeesState> | null {
  if (previousFingerprint === nextFingerprint) return null;
  return clearedFeeRecordPatch('absent');
}
